import React from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import { info as infoNotif, error as errorNotif } from "react-notification-system-redux";

import { statesConst } from "_constants";
import breakpoints from "_constants/breakpoints";
import { validateTrim } from "_helpers/formValidate";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  background-color: #ffffff;
  width: 100%;
  max-width: 660px;
  margin-bottom: 32px;
`;
const Title = styled.div`
  height: 47px;
  margin-left: 32px;
  margin-bottom: 14px;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 47px;
  color: #03589c;
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      font-size: 22px;
      text-align: center;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 18px;
        margin-left: 0px;
        margin-bottom: 0px;
      }
    `}
`;
const InfoForm = styled(Form)`
  padding: 48px 52px 52px 48px;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      padding: 0 28px;
      box-shadow: none;

      @media (max-width: ${breakpoints.mobileM}) {
        padding: 8px 16px;
      }
    `}
`;
const Group = styled(Form.Group)`
  &:not(:last-child) {
    margin-bottom: 18px;
  }
`;
const Label = styled(Form.Label)`
  height: 28px;
  margin-left: 8px;
  margin-bottom: 0;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 28px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;
const Control = styled(Form.Control)`
  font-family: "Montserrat";
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  border: 1px solid #a5aaaf;
  &:not(:last-child) {
    margin-bottom: 12px;
  }

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.75rem;
  }
`;
const Select = styled(Form.Select)`
  font-family: "Montserrat";
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  border: 1px solid #a5aaaf;

  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  background-color: #fff;
  background-clip: padding-box;
  border-radius: 0.25rem;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.75;
  }
`;
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 660px;
`;
const SaveButton = styled.button`
  width: 100%;
  max-width: 180px;
  height: 52px;
  margin: 0 7px;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  color: #fafaff;
  background-color: #f37217;
  position: relative;

  transition: 0.1s ease-in-out;

  &:disabled {
    background-color: #afafaf;
  }

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.825rem;
  }
`;
const CancelButton = styled(SaveButton)`
  border: 2px solid #ecedf1;
  color: #03589c;
  background-color: #ffffff;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.825rem;
  }
`;

const rules = {
  firstName: {
    required: "First Name is required",
    validate: validateTrim,
  },
  lastName: {
    required: "Last Name is required",
    validate: validateTrim,
  },
  address1: {
    required: "Address is required",
    maxLength: {
      value: 30,
      message: "Address must be at most 30 characters",
    },
    pattern: {
      value: /^(?!\s)(?!.*\s$)[a-zA-Z0-9\\/\- ]*$/,
      message: "Must not start/end with space and only allow letters, numbers, spaces, hyphens, and slashes",
    },
    validate: validateTrim,
  },
  address2: {
    maxLength: {
      value: 30,
      message: "Address must be at most 30 characters",
    },
    pattern: {
      value: /^(?!\s)(?!.*\s$)[a-zA-Z0-9\\/\- ]*$/,
      message: "Must not start/end with space and only allow letters, numbers, spaces, hyphens, and slashes",
    },
    validate: validateTrim,
  },
  city: {
    required: "City is required",
    maxLength: {
      value: 30,
      message: "City must be at most 30 characters",
    },
    pattern: {
      value: /^(?!\s)(?!.*\s$)[a-zA-Z/\- ]*$/,
      message: "Must not start/end with space and only allow letters, hyphens, and slashes",
    },
    // validate: validateTrim,
  },
  county: {
    maxLength: {
      value: 30,
      message: "County must be at most 30 characters",
    },
    pattern: {
      value: /^(?!\s)(?!.*\s$)[a-zA-Z/\-& ]*$/,
      message: "Must not start/end with space and only allow letters, hyphens, ampersands, and slashes",
    },
    // validate: validateTrim,
  },
  state: {
    required: "State is required",
  },
  zip: {
    required: "ZIP code is required",
    pattern: {
      value: /^\d{5}$/,
      message: "Invalid ZIP code (must be 5 digits)",
    },
    maxLength: {
      value: 5,
      message: "ZIP code must be exactly 5 digits",
    },
  },
};

function CustomerInfo({ userInfo, onSave }) {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      address: {
        address1: userInfo.address.address1,
        address2: userInfo.address.address2,
        city: userInfo.address.city,
        state: userInfo.address.state,
        zip: userInfo.address.zip,
        county: userInfo.address.county,
      },
    },
  });

  const getNestedError = (errors, path) => {
    return path.split(".").reduce((acc, key) => {
      if (acc && acc[key]) return acc[key];
      return null;
    }, errors);
  };

  const renderError = (errors, fieldPath) => {
    const error = getNestedError(errors, fieldPath);
    return error ? <Form.Control.Feedback type="invalid">{error.message}</Form.Control.Feedback> : null;
  };

  const handleChangeZip = (e) => {
    const cleanValue = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
    e.target.value = cleanValue;
  };

  const handleCancel = (e) => {
    e.preventDefault();
    reset();
    dispatch(infoNotif({ title: "Info", message: "Changes successfully canceled." }));
  };

  const onSubmit = (data) => {
    onSave({
      ...userInfo,
      ...data,
    });
  };

  const onError = () => {
    dispatch(errorNotif({ title: "Error", message: "Please, enter correct values" }));
  };

  return (
    <Container>
      <Title>Customer Info</Title>

      <InfoForm noValidate onSubmit={handleSubmit(onSubmit, onError)}>
        <Group>
          <Label>FIRST NAME</Label>
          <Control
            type="text"
            placeholder="Enter first name"
            isInvalid={!!errors.firstName}
            {...register("firstName", rules.firstName)}
          />
          {renderError(errors, "firstName")}
        </Group>

        <Group>
          <Label>LAST NAME</Label>
          <Control
            type="text"
            placeholder="Enter last name"
            isInvalid={!!errors.lastName}
            {...register("lastName", rules.lastName)}
          />
          {renderError(errors, "lastName")}
        </Group>

        <Label>MAILING ADDRESS</Label>
        <Group>
          <Control
            type="text"
            placeholder="Enter address"
            isInvalid={!!errors.address?.address1}
            {...register("address.address1", rules.address1)}
          />
          {renderError(errors, "address.address1")}
        </Group>

        <Group>
          <Control
            type="text"
            placeholder="Enter address 2"
            isInvalid={!!errors.address?.address2}
            {...register("address.address2", rules.address2)}
          />
          {renderError(errors, "address.address2")}
        </Group>

        <Group>
          <Control
            type="text"
            placeholder="Enter city"
            isInvalid={!!errors.address?.city}
            {...register("address.city", rules.city)}
          />
          {renderError(errors, "address.city")}
        </Group>

        <Group>
          <Select
            placeholder="Enter state"
            isInvalid={!!errors.address?.state}
            {...register("address.state", rules.state)}
          >
            <option value="">Select State</option>
            {Object.keys(statesConst).map((key) => (
              <option key={key} value={key}>
                {statesConst[key]}
              </option>
            ))}
          </Select>
          {renderError(errors, "address.state")}
        </Group>

        <Group>
          <Control
            inputMode="numeric"
            type="text"
            placeholder="Enter zip"
            isInvalid={!!errors.address?.zip}
            {...register("address.zip", rules.zip)}
            onInput={handleChangeZip}
            maxLength={5}
          />
          {renderError(errors, "address.zip")}
        </Group>

        <Group>
          <Control
            type="text"
            placeholder="Enter county"
            isInvalid={!!errors.address?.county}
            {...register("address.county", rules.county)}
          />
          {renderError(errors, "address.county")}
        </Group>

        <Buttons>
          <CancelButton type="button" onClick={handleCancel}>
            Cancel
          </CancelButton>
          <SaveButton type="submit" disabled={!isDirty}>
            Save changes
          </SaveButton>
        </Buttons>
      </InfoForm>
    </Container>
  );
}

CustomerInfo.propTypes = {
  userInfo: PropTypes.object,
  onSave: PropTypes.func,
};

export default CustomerInfo;
